import React, { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import Splide from '@splidejs/splide';
import { isDesktop } from '/Foundation/Common/code/Utils/Utils';
import Spinner from '/Foundation/Common/code/Views/Foundation/Common/Spinner';
import { hasImageFileEnding } from '/Foundation/Common/code/Utils/Utils';
import Notification from '/Foundation/Common/code/Views/Foundation/Common/Notifications/Notification';
import * as Sentry from '@sentry/react';

const PressRelease = (props) => {
  const { Text, BynderCollectionId, SpaceBelowComponent, Title, Translations, PressContact } = props;

  const [assets, setAssets] = useState([]);
  const [isDownloadingAllAssets, setIsDownloadingAllAssets] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState('');

  const slider = useRef();
  const title = useRef();
  const text = useRef();

  let splide;
  const abortController = new AbortController();
  const placeholders = isDesktop() ? 6 : 1;

  window.addEventListener('beforeunload', () => {
    abortController.abort();
  });

  useEffect(() => {
    getAssets();
  }, []);

  useEffect(() => {
    if (assets?.length > 0) {
      const splideSettings = {
        type: 'loop',
        start: 0,
        focus: 'center',
        autoWidth: true,
        arrows: false,
        pagination: true,
        clones: 1,
        updateOnMove: true,
        mediaQuery: 'min',
        breakpoints: {
          992: {
            destroy: true,
          },
        },
      };

      splide = new Splide(slider.current, splideSettings).mount();
    }
  }, [assets]);

  const getAssets = () => {
    fetch(`/api/sitecore/bynder/GetAssets?collectionId=${BynderCollectionId}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      signal: abortController.signal,
    })
      .then((resp) => resp.json())
      .then((data) => {
        setAssets(data);
      });
  };

  const copyText = () => {
    const textToCopy = title.current?.innerText + '\n\n' + text.current?.innerText;
    navigator.clipboard.writeText(textToCopy);
    setInfoMessage(Translations.CopyFeedback);
  };

  const downloadAsset = (e, asset) => {
    e.preventDefault();
    fetch(`/api/sitecore/bynder/downloadAsset?assetId=${asset.Id}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })
      .then((resp) => resp.json())
      .then((data) => {
        const a = document.createElement('a');
        a.href = data.DownloadLocation;
        const fileNameIncludesExtension = hasImageFileEnding(asset.Name);
        const fileName = fileNameIncludesExtension ? asset.Name : `${asset.Name}.${asset.Extension[0]}`;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      })
      .catch((error) => {
        console.log(error);
        setErrorMessage(Translations.ErrorMessage);
      });
  };

  const downloadAllAssets = (e) => {
    e.preventDefault();
    setIsDownloadingAllAssets(true);
    fetch(`/api/sitecore/bynder/downloadAllAssets?collectionId=${BynderCollectionId}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })
      .then((resp) => resp.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = 'Thule_PressRelease.zip';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        setIsDownloadingAllAssets(false);
      })
      .catch((error) => {
        console.log(error);
        setErrorMessage(Translations.ErrorMessage);
        setIsDownloadingAllAssets(false);
      });
  };

  return (
    <div className={`press-release ${SpaceBelowComponent ? 'space-below' : ''}`}>
      {errorMessage && (
        <Notification
          ShowIcon={true}
          IsToast={true}
          Type={'error'}
          Size={'lg'}
          Message={errorMessage}
          SetMessage={setErrorMessage}
          CloseAction={() => setErrorMessage('')}
        />
      )}
      {infoMessage && (
        <Notification
          IsToast={true}
          ShowIcon={true}
          Type={'success'}
          Size={'lg'}
          Message={infoMessage}
          SetMessage={setInfoMessage}
          CloseAction={() => setInfoMessage('')}
        />
      )}
      <div className="thule-container mb-9 mb-lg-12 col-md-10 col-xxl-8">
        <h1 className="heading1" ref={title}>
          {Title}
        </h1>
      </div>
      <div className="thule-container full-width-in-mobile">
        <div className="image-section mb-12">
          {(!assets || assets?.length === 0) && (
            <div className="spinner-container">
              <Spinner />
            </div>
          )}
          <div className="splide" ref={slider}>
            <div className="splide__track">
              <div className="splide__list">
                {assets?.length > 0 ? (
                  <>
                    {assets?.map((asset, i) => (
                      <div className="splide__slide item" key={i}>
                        <img src={asset.Thumbnails?.WebImage} alt={asset.Description} />
                        <div className="item-border">
                          {asset.Thumbnails?.WebImage && (
                            <div className="download-img-banner" onClick={(e) => downloadAsset(e, asset)}>
                              <i className="le-icon-download me-3"></i>
                              <span className="heading6">{Translations.DownloadImage}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    {[...Array(placeholders)].map((x, i) => (
                      <div className="img-placeholder item" key={i}></div>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="thule-container">
        <div className="d-lg-flex">
          <div className="action-menu me-6 mb-12 mb-lg-0">
            <div className="py-3 menu-item">
              <button className="action-btn px-0 btn py-4" onClick={copyText}>
                <i className="le-icon-copy me-3"></i> <span className="heading5">{Translations.CopyText}</span>
              </button>
            </div>
            <div className="py-3 menu-item">
              <button className="action-btn px-0 btn py-4" onClick={(e) => downloadAllAssets(e)}>
                <i className="le-icon-download me-3"></i>
                <span className="heading5">
                  {Translations.DownloadAll} {isDownloadingAllAssets && <Spinner />}
                </span>
              </button>
            </div>
            <div className="contact">
              <span className="heading5 my-5">{Translations.Contact}</span>
              <span className="paragraph mb-2">{Translations.Inquiries}</span>
              <div className="paragraph contact-details" dangerouslySetInnerHTML={{ __html: PressContact }}></div>
            </div>
          </div>
          <div dangerouslySetInnerHTML={{ __html: Text }} className="text-content paragraph" ref={text}></div>
        </div>
      </div>
    </div>
  );
};

const root = createRoot(document.getElementById('REACT_pressRelease'));
root.render(
  <Sentry.ErrorBoundary>
    <PressRelease {...window._THULEDATA._pressRelease} />
  </Sentry.ErrorBoundary>
);
